/**
 * Allows you to test if the viewport width is within specific breakpoints. We
 * do this by using the variables.js file that is shared with the CSS.
 *
 * By default, we have a variablePrefix of 'screen_'. This means if you run the following
 * ViewportTracker.below('mobile')
 * It will look up the screen size for the variable 'screen_mobile'. This specific test
 * will return true if the viewport width is less-than-or-equal-to the value of
 * 'screen_mobile'.
 *
 * Currently, this class requires the breakpoint variables to be formatted as: [0-9]+px
 * For example 100px is valid but the following are NOT: 100, 100%, 100em
 *
 * This module exposes a singleton. Before it is used init(...) must be called on it.
 */
class ViewportTracker {
	private variables?: Record<string, string|number>;
	private variablePrefix?: string;

	init(variables: Record<string, string|number>, variablePrefix = 'screen_'): void {
		this.variables = variables;
		this.variablePrefix = variablePrefix;
	}

	/**
	 * Test is exclusive (<), unless otherwise specified
	 * @param {string} name
	 * @param {boolean} inclusive
	 */
	below(name: string, inclusive: boolean = false): boolean {
		return this.between({maxName: name}, {maxInclusive: inclusive});
	}

	/**
	 * Test is exclusive (>), unless otherwise specified
	 * @param {string} name
	 * @param {boolean} inclusive
	 */
	above(name: string, inclusive: boolean = false): boolean {
		return this.between({minName: name}, {minInclusive: inclusive});
	}

	/**
	 * Test if viewport width is between the given min and max.
	 *
	 * @param {MinMaxNames} minMaxNames
	 * @param {MinMaxInclusivity} minMaxInclusivity
	 */

	between(minMaxNames: {minName?: string, maxName?: string}, minMaxInclusivity?: {minInclusive?: boolean, maxInclusive?: boolean}): boolean {
		const min = minMaxNames.minName ? this.getPixelSize(minMaxNames.minName) : undefined;
		const max = minMaxNames.maxName ? this.getPixelSize(minMaxNames.maxName) : undefined;
		const minInclusive = minMaxInclusivity?.minInclusive ?? false;
		const maxInclusive = minMaxInclusivity?.maxInclusive ?? false;

		// NOTE: I was going to program this to use window.matchMedia when available,
		//		but it seems like it would be slower so sticking with code below unless
		//		we find it prone to error

		const viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth);

		return (min === undefined || min < viewportWidth || (minInclusive && min === viewportWidth))
			&& (max === undefined || max > viewportWidth || (maxInclusive && max === viewportWidth));
	}

	private getPixelSize(name: string): number {
		const varName = this.variablePrefix ? this.variablePrefix + name : name;

		const size = this.variables?.[varName];
		if (typeof size !== 'string') {
			// No value
			throw new Error(`Unable to get pixel size of "${name}" because no variable named: ${varName}`);
		}

		// Confirm size is a number followed by px
		const matchFormat = size.match(/([0-9]+)px/);
		if (matchFormat === null) {
			throw new Error(`Unable to get pixel size of "${name}" because it does not match required format. Value is requires to be a number and followed by "px". Value is: ${size}`);
		}

		return parseInt(matchFormat[1], 10);
	}
}

export default new ViewportTracker();
